/**
 * arbi auth profile [--name <n>] [--family <n>] [--picture <path>]
 * arbi auth change-email <new-email>          — request verification code
 * arbi auth confirm-email <code>              — confirm the new email
 * arbi auth delete-account                    — start 30-day deletion timer
 * arbi auth cancel-deletion                   — cancel an in-flight deletion
 * arbi auth sso-config                        — show whether the deployment supports SSO
 *
 * Self-service user-account operations. None of these were previously
 * reachable from the CLI — agents that needed to rotate credentials or
 * retire an account had to bounce through the web UI. The endpoints all
 * live under /v1/user/* and are wired via the typed openapi-fetch client.
 */

import { Command } from 'commander'
import { readFileSync } from 'node:fs'
import { resolve } from 'node:path'
import { runAction, resolveAuth, printJson, suggestSubcommandAndExit } from '../helpers.js'
import { success, error, label, dim } from '../output.js'

export function registerAuthCommand(program: Command): void {
  const auth = program
    .command('auth')
    .description('Self-service account ops: profile, change-email, delete-account, sso-config')

  // ── profile ──────────────────────────────────────────────────────────────
  // /v1/user/profile is PATCH-only — there's no read endpoint that returns
  // the display name pair. To "show" the current profile we look at the
  // /v1/user/contacts entry that points at ourselves (which carries
  // given/family/picture); that's the only path that doesn't require a
  // separate user-read endpoint. Falls back to `arbi status` if the user
  // hasn't added themselves as a contact.
  auth
    .command('profile')
    .description('Show or update your display profile (given/family name, picture)')
    .option('--name <given>', 'New given name')
    .option('--family <family>', 'New family name (empty string clears it)')
    .option(
      '--picture <path-or-url>',
      'Profile picture (local path → base64 inline; URL → stored verbatim)'
    )
    .action((opts: { name?: string; family?: string; picture?: string }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const noUpdates = !opts.name && opts.family === undefined && !opts.picture
        if (noUpdates) {
          // No flags → show whatever the workspace-users list exposes about
          // the current user (the only place the backend surfaces given/
          // family/picture in a single round-trip).
          const myExtId = arbi.session.getState().userExtId
          const res = await arbi.fetch.GET('/v1/workspace/users')
          if (res.error || !res.data) {
            error(
              'No profile-read endpoint exists. To inspect: arbi status --json or arbi contacts --json'
            )
            process.exit(1)
          }
          const me = (res.data as Array<Record<string, unknown>>).find(
            (u) => u.external_id === myExtId
          )
          const profile = me ?? { external_id: myExtId }
          for (const [k, v] of Object.entries(profile)) {
            label(`${k}:`, typeof v === 'object' ? JSON.stringify(v) : String(v))
          }
          dim('\nPass --name/--family/--picture to update.')
          return
        }

        // Read local picture file → data URL (the backend accepts both URLs
        // and `data:image/...;base64,...` strings).
        let picture = opts.picture
        if (picture && !/^https?:|^data:/.test(picture)) {
          try {
            const bytes = readFileSync(resolve(picture))
            const ext = picture.toLowerCase().split('.').pop()
            const mime = ext === 'png' ? 'image/png' : ext === 'gif' ? 'image/gif' : 'image/jpeg'
            picture = `data:${mime};base64,${bytes.toString('base64')}`
          } catch {
            error(`Cannot read picture file: ${picture}`)
            process.exit(3)
          }
        }

        const body: Record<string, string | null> = {}
        if (opts.name !== undefined) body.given_name = opts.name
        if (opts.family !== undefined) body.family_name = opts.family
        if (picture !== undefined) body.picture = picture

        const res = await arbi.fetch.PATCH('/v1/user/profile', { body })
        if (res.error) throw new Error(`Profile update failed: ${JSON.stringify(res.error)}`)
        success('Profile updated.')
      })()
    )

  // ── change-email ─────────────────────────────────────────────────────────
  auth
    .command('change-email <new-email>')
    .description('Request a verification code for an email change (SSO users update at IdP)')
    .action((newEmail: string) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const res = await arbi.fetch.POST('/v1/user/request-email-change', {
          body: { new_email: newEmail },
        })
        if (res.error) throw new Error(`request-email-change failed: ${JSON.stringify(res.error)}`)
        success(`Verification code sent to ${newEmail}.`)
        dim(`Confirm with: arbi auth confirm-email <code>`)
      })()
    )

  // ── confirm-email ────────────────────────────────────────────────────────
  auth
    .command('confirm-email <code>')
    .description('Confirm an email change with the code from change-email')
    .option('--new-email <email>', 'The new email being confirmed (required)')
    .action((code: string, opts: { newEmail?: string }) =>
      runAction(async () => {
        if (!opts.newEmail) {
          error('Missing --new-email. The new address is required to complete the change.')
          process.exit(1)
        }
        const { arbi } = await resolveAuth()
        const res = await arbi.fetch.POST('/v1/user/confirm-email-change', {
          body: { new_email: opts.newEmail, verification_code: code },
        })
        if (res.error) throw new Error(`confirm-email-change failed: ${JSON.stringify(res.error)}`)
        success(`Email changed to ${opts.newEmail}.`)
      })()
    )

  // ── delete-account / cancel-deletion REMOVED ────────────────────────────
  // The /v1/user/request-deletion and /v1/user/cancel-deletion endpoints
  // never landed on the backend. I shipped these commands against schema
  // entries that were either hand-edited or generated against a different
  // backend snapshot. The typecheck on the frontend (DeleteAccountDialog /
  // DeletionPendingBanner) caught it; both surfaces are removed in the same
  // PR. Re-add when the backend ships the endpoints for real.

  // ── sso-config ───────────────────────────────────────────────────────────
  // The actual pre-login probe: does this deployment support SSO at all, and
  // which Auth0 tenant? Doesn't need authentication.
  //
  // (We do NOT expose /v1/user/sso-status because that endpoint requires an
  // Auth0 sso_token and only returns useful info AFTER the device-code flow
  // completes — it's an internal step of the SSO login machinery, not a CLI
  // workflow agents would call directly.)
  auth
    .command('sso-config')
    .description('Show SSO config for this deployment (sso_enabled, Auth0 domain, audience)')
    .option('--json', 'Output as JSON')
    .action((opts: { json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        // openapi-fetch's strict overload resolution loses the response shape
        // for some path/op combos (the union doesn't narrow); fall back to a
        // typed accessor so we still get the .data / .error contract.
        const res = (await arbi.fetch.GET('/v1/user/sso-config')) as {
          data?: Record<string, unknown>
          error?: unknown
        }
        if (res.error) throw new Error(`sso-config failed: ${JSON.stringify(res.error)}`)
        if (opts.json) {
          printJson(res.data)
          return
        }
        for (const [k, v] of Object.entries(res.data as Record<string, unknown>)) {
          label(`${k}:`, typeof v === 'object' ? JSON.stringify(v) : String(v))
        }
        dim('\nTo log in: arbi login --sso -e <email> -p <master-password>')
        dim('(--sso still needs --password — the master password is the E2E key seed.)')
      })()
    )

  // Default action: profile show, with typo suggestion for unknown subcommands.
  auth
    .allowUnknownOption(true)
    .allowExcessArguments(true)
    .action(async () => {
      const tail = auth.args ?? []
      const subcommands = auth.commands.map((c) => c.name())
      const firstNonFlag = tail.find((a) => !a.startsWith('-'))
      if (firstNonFlag && !subcommands.includes(firstNonFlag)) {
        suggestSubcommandAndExit('auth', firstNonFlag, subcommands)
      }
      await auth.commands.find((c) => c.name() === 'profile')!.parseAsync(tail, { from: 'user' })
    })
}
